import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { Helmet } from "react-helmet-async";
import { loginBg } from "../../assets/images";
import { openAppIcon } from "../../assets/icons";
import {
  CACHE_KEYS,
  EMPTY_ARRAY,
  EMPTY_STRING,
  ROUTES,
  VALIDATION_MESSAGES,
  REGEX,
  ZERO
} from "../../constants";
import {
  fetchUserProfileById,
  selectIsAuthenticated,
  selectUser,
} from "../UserProfile";
import { Alert } from "../../components/molecules";
import { selectAlert } from "../../components/organisms/Alert";
import { setShouldShowNav, selectShouldShowNav } from "../AuthScreens";
import Spinner, {
  SPINNER_NAMES,
  addSpinner,
  removeSpinner,
} from "../../components/organisms/Spinner";
import {
  createOrganization,
  fetchOrganizations,
  selectOrganizationsFilters,
} from "../Organizations";
import { isEmpty } from "lodash";

const { ORGANIZATION } = CACHE_KEYS;
const { HOME } = ROUTES;
const { CREATE_WORKSPACE, FETCH_ORGANIZATIONS } = SPINNER_NAMES;
const CREATE_WORKSPACE_ERROR_MESSAGE =
  "Unable to create workspace. Please try after sometime.";
const { WHITESPACES_AT_START, SPECIAL_CHARACTERS } = REGEX;
const {
  VALIDATE_NAME,
  NAME_SHOULD_NOT_CONTAINS,
  NAME_REQUIRED,
  MAX_CHAR_LENGTH,
  MIN_CHAR_LENGTH,
} = VALIDATION_MESSAGES;
const MAX_NAME_LENGTH = 25;
const MIN_NAME_LENGTH = 3;

const SelectWorkspace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedAlert = useSelector(selectAlert);
  const shouldShowNav = useSelector(selectShouldShowNav);
  const selectedUser = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const organizationFilters = useSelector(selectOrganizationsFilters);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [organizations, setOrganizations] = useState(EMPTY_ARRAY);
  const [showWorkspaceField, setShowWorkspaceField] = useState(false);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const validationSchema = Yup.object({
    workspaceName: Yup.string()
      .matches(WHITESPACES_AT_START, VALIDATE_NAME)
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAINS)
      .max(MAX_NAME_LENGTH, MAX_CHAR_LENGTH)
      .min(MIN_NAME_LENGTH, MIN_CHAR_LENGTH)
      .required(NAME_REQUIRED),
  });

  const handleOnWorkspaceClick = useCallback(
    (organizationName, organizationId) => {
      const organization = {
        id: organizationId,
        name: organizationName,
      };
      sessionStorage.setItem(ORGANIZATION, JSON.stringify(organization));
      dispatch(setShouldShowNav(true));
      setTimeout(navigate(HOME, { replace: true }), 1000);
    },
    [dispatch, navigate]
  );

  const handleCreateNewWorkspace = useCallback(
    ({ workspaceName }) => {
      dispatch(addSpinner(CREATE_WORKSPACE));
      setIsSpinnerActive(true);
      const { name, email, sub } = selectedUser;
      const data = {
        name,
        email,
        sub,
        organization: workspaceName,
      };
      dispatch(createOrganization(data)).then((res) => {
        const { payload } = res;
        if (!isEmpty(payload)) {
          handleOnWorkspaceClick(workspaceName, payload.id);
          dispatch(removeSpinner(CREATE_WORKSPACE));
          setIsSpinnerActive(false);
          dispatch(fetchUserProfileById(selectedUser.sub));
        } else setAlertData({ message: CREATE_WORKSPACE_ERROR_MESSAGE });
      });
    },
    [dispatch, handleOnWorkspaceClick, selectedUser]
  );

  useEffect(() => {
    dispatch(fetchOrganizations(organizationFilters));
  }, [dispatch, organizationFilters]);

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (alertData) {
      dispatch(removeSpinner(CREATE_WORKSPACE));
      setIsSpinnerActive(false);
    }
  }, [alertData, dispatch]);

  useEffect(() => {
    if (selectedUser) setOrganizations(selectedUser.organizations);
  }, [selectedUser]);

  useEffect(() => {
    const isWorkspaceSelected = !!JSON.parse(
      sessionStorage.getItem(ORGANIZATION)
    );
    if (isAuthenticated && isWorkspaceSelected) {
      dispatch(setShouldShowNav(true));
      setTimeout(navigate(HOME, { replace: true }), 1000);
    }
  }, [dispatch, navigate, isAuthenticated]);

  return (
    <>
      <Helmet>
        <title>Workspaces | TavaJS</title>
      </Helmet>
      {!shouldShowNav && (
        <div className="auth-container min-h-screen relative flex items-center">
          <img
            src={loginBg}
            className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
            alt="login-bg"
          />
          <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
            <div className="auth-header mb-8">
              {alertData && (
                <div className="my-2">
                  <Alert
                    type={alertData.type}
                    message={alertData.message}
                    handleOnClose={setAlertData}
                  />
                </div>
              )}
              <h1 className="text-2xl leading-9 font-bold mb-5 text-center">
                Create a new workspace
              </h1>
              {showWorkspaceField ? (
                <Formik
                  initialValues={{ workspaceName: EMPTY_STRING }}
                  validationSchema={validationSchema}
                  onSubmit={handleCreateNewWorkspace}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    handleChange,
                    touched,
                    errors,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="form-group mb-6">
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Workspace Name"
                          name="workspaceName"
                          onChange={handleChange}
                        />
                        {touched.workspaceName && errors.workspaceName && (
                          <div className="font-medium text-red-500 text-sm">
                            {errors.workspaceName}
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary block w-full text-center mb-2 disabled:cursor-not-allowed"
                        disabled={isSpinnerActive}
                      >
                        <Spinner
                          name={CREATE_WORKSPACE}
                          setIsSpinnerActive={setIsSpinnerActive}
                        >
                          Continue
                        </Spinner>
                      </button>
                    </Form>
                  )}
                </Formik>
              ) : (
                <button
                  className="btn btn-primary block text-center w-full"
                  type="button"
                  onClick={() => setShowWorkspaceField(true)}
                >
                  Create a new workspace
                </button>
              )}
            </div>
            <div className="separator flex items-center my-6">
              <div className="flex-1 border-t border-white" />
              <span className="text-sm leading-5 font-normal px-2">OR</span>
              <div className="flex-1 border-t border-white" />
            </div>
            <div className="auth-header mb-8">
              <h1 className="text-2xl leading-9 font-bold mb-2 text-center">
                Open a workspace
              </h1>
              <Spinner name={FETCH_ORGANIZATIONS}>
                {organizations?.map(({ name, usersCount, id }) => (
                <div
                  key={id}
                  className="flex hover:shadow-lg select-none p-3 rounded-md border-gray-300 border my-6 hover:border-gray-500"
                >
                  <div className="left">
                    <div className="text-indigo-600 font-semibold text-xl">
                      {name}
                    </div>
                    <div className="text-gray-300">
                      {`${usersCount} ${usersCount > 1 ? "members" : "member"}`}
                    </div>
                  </div>
                  <div className="right m-auto mr-0">
                    <button
                      type="button"
                      onClick={() => handleOnWorkspaceClick(name, id)}
                    >
                      <img
                        src={openAppIcon}
                        alt="openApp"
                        className="h-8 w-8 text-gray-600"
                      />
                    </button>
                  </div>
                </div>
              ))}
                {organizations?.length === ZERO && (
                  <h1 className="text-2xl leading-9 font-bold mb-2 text-center">
                    No Organizations found
                  </h1>
                )}
              </Spinner>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SelectWorkspace;
